import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DataService } from '../../services/data.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

// Define interface for participant metadata from API
interface ParticipantMetadata {
  participantIdentifier: string;
  trialSequenceNumber: number;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
}

// Define interface for participant responses from API
interface ParticipantResponseData {
  participantIdentifier: string;
  trialSequenceNumber: number;
  uploadedBy: string;
  createdAt: string;
  responses: any[];
}

@Component({
  selector: 'app-excel-export-button',
  imports: [CommonModule],
  templateUrl: './excel-export-button.component.html',
  styleUrl: './excel-export-button.component.scss'
})
export class ExcelExportButtonComponent implements OnInit {
  @Input() trialSequenceNumber: number = 1; // Default to trial sequence 1
  
  completedParticipants: ParticipantMetadata[] = [];
  isLoading = false;
  participantCount = 0;
  private loadingPromise: Promise<void> | null = null; // Prevent concurrent loading
  
  // Progress tracking for export
  isExporting = false;
  exportProgress = 0;
  currentParticipant = '';
  currentParticipantIndex = 0;
  totalParticipants = 0;
  
  // Make Math available in template
  Math = Math;
  
  // API URLs - using environment configuration
  private readonly BASE_API_URL = environment.baseApiUrl;
  private readonly GET_ALL_PARTICIPANTS_URL = `${this.BASE_API_URL}/getAllParticipants`;
  private readonly GET_PARTICIPANT_RESPONSES_URL = `${this.BASE_API_URL}/getParticipantResponses`;
  
  constructor(
    private http: HttpClient,
    private dataService: DataService,
    private oidcSecurityService: OidcSecurityService
  ) {}

  async ngOnInit() {
    await this.loadCompletedParticipants();
  }

  async ngOnChanges() {
    // Reload when trial sequence number changes
    await this.loadCompletedParticipants();
  }

  async loadCompletedParticipants() {
    // Prevent concurrent calls
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this._loadCompletedParticipants();
    try {
      await this.loadingPromise;
    } finally {
      this.loadingPromise = null;
    }
  }

  // Public method to refresh data (can be called from parent components)
  async refresh() {
    await this.loadCompletedParticipants();
  }

  private async _loadCompletedParticipants() {
    try {
      this.isLoading = true;
      
      // Clear the array at the start of each load
      this.completedParticipants = [];
      
      // Get authentication token
      const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
      if (!token) {
        console.warn('No authentication token available for API call');
        return;
      }

      // Prepare headers with Authorization token
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      // Call API to get all participants for this trial sequence
      // Using forTesting from environment configuration
      const apiUrl = `${this.GET_ALL_PARTICIPANTS_URL}?forTesting=${environment.forTesting}&trialSequenceNumber=${this.trialSequenceNumber}`;
      
      const response: any = await firstValueFrom(
        this.http.get(apiUrl, { headers })
      );

      if (response.success && response.data && response.data.participants) {
        this.completedParticipants = response.data.participants;
        this.participantCount = this.completedParticipants.length;
        
        console.log(`Loaded ${this.participantCount} participants for trial sequence ${this.trialSequenceNumber}`);
      } else {
        console.warn('No participants found in API response');
        this.participantCount = 0;
      }

    } catch (error) {
      console.error('Error loading participants from API:', error);
      this.participantCount = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async exportExcel() {
    try {
      if (this.completedParticipants.length === 0) {
        alert(`No completed participants found for trial sequence ${this.trialSequenceNumber}`);
        return;
      }

      this.isLoading = true;
      this.isExporting = true;
      this.exportProgress = 0;
      this.currentParticipantIndex = 0;
      this.totalParticipants = this.completedParticipants.length;
      
      // Get authentication token
      const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
      if (!token) {
        alert('Authentication required. Please log in first.');
        return;
      }

      // Prepare headers
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      });

      // Fetch detailed responses for each participant
      const participantResponses: any[] = [];
      
      for (let i = 0; i < this.completedParticipants.length; i++) {
        const participant = this.completedParticipants[i];
        this.currentParticipant = participant.participantIdentifier;
        this.currentParticipantIndex = i + 1;
        
        try {
          const responseUrl = `${this.GET_PARTICIPANT_RESPONSES_URL}?participantIdentifier=${participant.participantIdentifier}&forTesting=${environment.forTesting}`;
          
          const response: any = await firstValueFrom(
            this.http.get(responseUrl, { headers })
          );

          if (response.success && response.data && response.data.responses) {
            participantResponses.push({
              participantIdentifier: response.data.participantIdentifier,
              uploadedBy: response.data.uploadedBy,
              trialSequenceNumber: response.data.trialSequenceNumber,
              taskData: response.data.responses // Map API 'responses' to expected 'taskData'
            });
          } else {
            console.warn(`No responses found for participant ${participant.participantIdentifier}`);
          }
        } catch (error) {
          console.error(`Error fetching responses for participant ${participant.participantIdentifier}:`, error);
        }
        
        // Update progress
        this.exportProgress = Math.round(((i + 1) / this.totalParticipants) * 100);
      }

      if (participantResponses.length === 0) {
        alert('No response data found for export');
        return;
      }

      // Set progress to 100% and show completion message
      this.currentParticipant = 'Generating CSV file...';
      this.exportProgress = 100;

      // Export using the data service (same interface as before)
      await this.dataService.exportResponsesAsExcel(participantResponses, this.trialSequenceNumber);
      
    } catch (error) {
      console.error('Error exporting Excel file:', error);
      alert('Failed to export Excel file. Please try again.');
    } finally {
      this.isLoading = false;
      this.isExporting = false;
      this.exportProgress = 0;
      this.currentParticipant = '';
      this.currentParticipantIndex = 0;
      this.totalParticipants = 0;
    }
  }
} 